import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Trash2,
} from "lucide-react";

import {
  TextField,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { CustomConfirmDialog } from "./CustomConfirmDialog";
import { toast } from "react-toastify";

export const TourLinksModal = ({ threadId, isOpen, onClose, axiosInstance }) => {
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [total, setTotal] = useState(0);
  const [linkLoading, setLinkLoading] = useState(false);

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyPage, setPropertyPage] = useState(1);
  const [propertyHasMore, setPropertyHasMore] = useState(true);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [unitOptions, setUnitOptions] = useState([]);
  const [unitPage, setUnitPage] = useState(1);
  const [unitHasMore, setUnitHasMore] = useState(true);
  const [unitLoading, setUnitLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const [touredOn, setTouredOn] = useState(dayjs()); // default today

  const resetForm = () => {
    setSelectedProperty(null);
    setSelectedUnit(null);
    setPropertyOptions([]);
    setUnitOptions([]);
    setTouredOn(dayjs());
  };

  const resetLinks = () => {
    setLinks([])
    setPage(1)
    setTotal(0)
    setLinkLoading(false)
  }


  // --- fetch links with page adjustment ---
  const fetchLinks = async (pageNum = 1) => {
    if (linkLoading) return;
    setLinkLoading(true);
    try {
      const res = await axiosInstance.get(`/toured-property`, {
        params: { thread_id: threadId, page: pageNum, limit },
      });

      let items = res.data?.items || [];
      const totalItems = res.data?.total || 0;

      // If current page has no items and it's not the first page, fetch previous page
      if (items.length === 0 && pageNum > 1) {
        return fetchLinks(pageNum - 1);
      }

      setLinks(items);
      setTotal(totalItems);
      setPage(pageNum);

    } catch (err) {
      console.error("Failed to fetch tour links:", err);
      toast.error("Failed to fetch toured properties");
    }
    finally {
      setLinkLoading(false);
    }
  };

  // --- other fetches ---
  const fetchProperties = async (page = 1, search = "") => {
    if (propertyLoading) return;
    setPropertyLoading(true);
    try {
      const res = await axiosInstance.get(`/properties/search`, {
        params: { q: search, page, limit: 10 },
      });
      const newItems = res.data.items || [];
      setPropertyOptions(
        page === 1 ? newItems : [...propertyOptions, ...newItems]
      );
      setPropertyHasMore(res.data.hasMore);
      setPropertyPage(page);
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    } finally {
      setPropertyLoading(false);
    }
  };

  const fetchUnits = async (propertyId, page = 1) => {
    if (unitLoading) return;
    setUnitLoading(true);
    try {
      const res = await axiosInstance.get(`/properties/units`, {
        params: { property_id: propertyId, page, limit: 100 },
      });
      const newItems = res.data.items || [];
      setUnitOptions(page === 1 ? newItems : [...unitOptions, ...newItems]);
      setUnitHasMore(res.data.hasMore);
      setUnitPage(page);
    } catch (err) {
      console.error("Failed to fetch units:", err);
    } finally {
      setUnitLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedProperty) {
      setUnitOptions([]);
      setSelectedUnit(null);
      return;
    }
    fetchUnits(selectedProperty.id, 1);
  }, [selectedProperty]);

  useEffect(() => {
    if (!isOpen) return;
    resetLinks()
    resetForm()
    fetchLinks(1);
  }, [isOpen, threadId]);


  const handleAdd = async () => {
    try {
      await axiosInstance.post(`/toured-property/create`, {
        thread_id: threadId,
        property_id: selectedProperty.id,
        unit_id: selectedUnit?.id,
        toured_on: touredOn ? touredOn.toISOString() : null,
      });
      toast.success("Toured property added successfully!");
      fetchLinks(1);
      resetForm();
    } catch (err) {
      console.error("Failed to add tour link:", err);
      toast.error("Failed to add toured property");
    }
  };

const handleDelete = async (id) => {
  CustomConfirmDialog({
    title: "Are you sure?",
    text: `This will delete the toured property record permanently.`,
    icon: "warning",
    confirmText: "Yes, delete it",
    cancelText: "Cancel",
    toastMessage: "The toured property deleted successfully!",
    toastError: "Failed to delete toured property",
    onConfirm: async () => {
      await axiosInstance.delete(`/toured-property/${id}`);
      fetchLinks(page);
    },
  });
};

  const formatDate = (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "");

  // --- UI ---
  return isOpen ? (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-6">
          Toured Properties
        </h2>

        {/* Input Form */}
        <div className="flex gap-3 items-end mb-2">
          {/* Property Autocomplete */}
          <Autocomplete
            size="small"
            className="flex-[2]"
            value={selectedProperty}
            onChange={(e, newValue) => setSelectedProperty(newValue)}
            onOpen={() => fetchProperties(1)}
            onInputChange={(e, value) => fetchProperties(1, value)}
            options={propertyOptions}
            getOptionLabel={(option) => option.name || ""}
            loading={propertyLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property"
                placeholder="Search property..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {propertyLoading && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 50 &&
                  propertyHasMore &&
                  !propertyLoading
                ) {
                  fetchProperties(propertyPage + 1);
                }
              },
            }}
          />

          {/* Unit Autocomplete */}
          {selectedProperty && (
            <Autocomplete
              size="small"
              className="flex-1"
              value={selectedUnit}
              onChange={(e, newValue) => setSelectedUnit(newValue)}
              onOpen={() => fetchUnits(selectedProperty.id, 1)}
              options={unitOptions}
              getOptionLabel={(option) =>
                `${option.model_name || ""} - ${option.unit || ""}`
              }
              loading={unitLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit"
                  placeholder="Select unit..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {unitLoading && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 50 &&
                    unitHasMore &&
                    !unitLoading
                  ) {
                    fetchUnits(selectedProperty.id, unitPage + 1);
                  }
                },
              }}
            />
          )}

          {/* DatePicker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Toured On"
              value={touredOn}
              format="MM/DD/YYYY"
              onChange={(newValue) => setTouredOn(newValue)}
              slotProps={{
                textField: { size: "small" },
              }}
            />
          </LocalizationProvider>

          {/* Add Button */}
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-2 flex items-center justify-center gap-1 text-md cursor-pointer"
            onClick={handleAdd}
            disabled={!selectedProperty || !selectedUnit}
          >
            Add
          </button>
        </div>

        {/* Tour Links Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {links.map((l) => (
            <div
              key={l.id}
              className="rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition bg-gray-50"
            >
              <h3 className="font-semibold text-gray-800">{l.property?.name}</h3>
              <p className="text-sm text-gray-600">
                {l.unit?.model_name} - {l.unit?.unit}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Toured on: {formatDate(l.toured_on)}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => handleDelete(l.id)}
                  className="mt-3 text-sm text-red-600 hover:text-red-800"
                  title="delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Tour Links Loading*/}
        {linkLoading &&
        <div className="flex justify-center items-center gap-3 text-sm">
          <CircularProgress size={18} /> <span>Loading...</span>
        </div>
        }

        {/* Pagination */}
        {total > limit && (
          <div className="flex justify-center mt-6 gap-3 items-center text-sm">
            <button
              onClick={() => fetchLinks(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Prev
            </button>
            <span className="text-gray-600">
              Page {page} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => fetchLinks(page + 1)}
              disabled={page * limit >= total}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        )}

        {/* Close */}
        <div className="flex justify-end mt-6">
          <button
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
            onClick={()=>{
              resetForm()
              resetLinks()
              onClose()
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;
};